import { PushNotifications, type ActionPerformed, type PushNotificationSchema } from '@capacitor/push-notifications'
import { Capacitor, registerPlugin } from '@capacitor/core'
import { isAgentChatNativeApp } from './nativeExternalOpen'
import { rememberPendingNotificationNav } from './notificationNav'
import {
  saveLocalChat,
  saveLocalMessages,
  saveLocalTopics,
} from './localReadStore'
import {
  startNotificationOpenTiming,
  updateNotificationOpenTiming,
} from './notificationOpenDiagnostics'
import type { Chat, Message, Topic } from './api'

const CHANNEL_ID = 'agentchat_turns'
const EMAIL_DRAFT_CHANNEL_ID = 'agentchat_email_draft_alerts'
const OPEN_EVENT = 'agentchat:native-push-open'
const PREFETCHED_EVENT = 'agentchat:native-notification-prefetched'
const MAX_PENDING_OPEN_URLS = 5

let listenersBound = false
let bootSyncStarted = false
let pendingOpenUrls: PendingNativePushOpen[] = []
const nativeFullBundleTargets = new Set<string>()

type NativePermission = 'unavailable' | 'prompt' | 'granted' | 'denied'
type NativePushOpenDetail = {
  url: string
  handled: boolean
  diagnosticOpenId?: string | null
  diagnosticStartMs?: number | null
}
type PendingNativePushOpen = {
  url: string
  diagnosticOpenId?: string | null
  diagnosticStartMs?: number | null
}
type NativeNotificationPrefetchedDetail = {
  chat_id: string
  topic_id: string | null
  message_id: string | null
  chat: Chat | null
  topics: Topic[]
  messages: Message[]
}
type NativeNotificationTarget = {
  chatId: string
  topicId: string | null
}
type NativeNotificationPrefetchTarget = NativeNotificationTarget & {
  messageId: string | null
}
type NotificationOpenBundle = {
  chat_id?: string | null
  topic_id?: string | null
  message_id?: string | null
  chat?: Chat | null
  topics?: Topic[]
  messages?: Message[]
}
type NativeAppInfo = {
  packageName: string
  versionCode: number | string
  versionName: string
}
type AgentChatAppInfoPlugin = {
  getInfo(): Promise<NativeAppInfo>
}
type NativeNotificationCacheItem = {
  received_at_ms?: number | null
  message_id?: string | null
  title?: string | null
  body?: string | null
  tag?: string | null
  data?: Record<string, unknown> | null
  open_bundle?: NotificationOpenBundle | null
}
type AgentChatNativeNotificationCachePlugin = {
  drainPending(): Promise<{ items?: NativeNotificationCacheItem[] | null }>
}

const AgentChatAppInfo = registerPlugin<AgentChatAppInfoPlugin>('AgentChatAppInfo')
const AgentChatNativeNotificationCache = registerPlugin<AgentChatNativeNotificationCachePlugin>(
  'AgentChatNativeNotificationCache',
)

function nativePushUsable(): boolean {
  return isAgentChatNativeApp()
}

function nativeNotificationTargetFromTag(tag: string | null): NativeNotificationTarget | null {
  if (!tag?.startsWith('chat-')) return null
  const raw = tag.slice('chat-'.length)
  const topicMarker = '-topic-'
  const topicIndex = raw.indexOf(topicMarker)
  if (topicIndex < 0) return { chatId: raw, topicId: null }
  const chatId = raw.slice(0, topicIndex)
  const topicId = raw.slice(topicIndex + topicMarker.length)
  if (!chatId || !topicId) return null
  return { chatId, topicId }
}

function chatIdFromPathname(pathname: string): string | null {
  const match = pathname.match(/^\/chats\/([^/]+)$/)
  return match?.[1] ? decodeURIComponent(match[1]) : null
}

function currentNativeLocationUrl(): URL | null {
  if (typeof window === 'undefined') return null
  try {
    const location = window.location
    return new URL(`${location.pathname || '/'}${location.search || ''}`, location.origin)
  } catch {
    return null
  }
}

function nativeNotificationTargetIsVisible(target: NativeNotificationTarget): boolean {
  const url = currentNativeLocationUrl()
  if (!url) return false
  const currentChatId = url.searchParams.get('chat_id') || chatIdFromPathname(url.pathname)
  if (currentChatId !== target.chatId) return false
  const currentTopicId = url.searchParams.get('topic_id') || url.searchParams.get('topic') || null
  return (currentTopicId || null) === (target.topicId || null)
}

function nativeNotificationUrlFromTarget(
  target: NativeNotificationTarget,
  messageId: string,
): string {
  const params = new URLSearchParams({ chat_id: target.chatId })
  params.set('topic_id', target.topicId ?? '')
  if (messageId) {
    params.set('message_id', messageId)
  }
  params.set('notification_open', '1')
  return `/chats/${encodeURIComponent(target.chatId)}?${params.toString()}`
}

function canonicalNativePushUrl(
  rawUrl: string | null,
  data: Record<string, unknown>,
  taggedTarget: NativeNotificationTarget | null,
): string {
  const explicitChatId = stringValue(data.chat_id)
  const explicitTopicId = stringValue(data.topic_id) || stringValue(data.topic)
  const explicitMessageId = stringValue(data.message_id) || stringValue(data.message)
  let url: URL
  try {
    url = new URL(rawUrl?.trim() || '/chats', window.location.origin)
  } catch {
    url = new URL('/chats', window.location.origin)
  }

  const chatId = (
    explicitChatId ||
    url.searchParams.get('chat_id') ||
    chatIdFromPathname(url.pathname) ||
    taggedTarget?.chatId ||
    ''
  )
  if (!chatId) return `${url.pathname}${url.search}`

  const topicId = (
    explicitTopicId ||
    url.searchParams.get('topic_id') ||
    url.searchParams.get('topic') ||
    taggedTarget?.topicId ||
    ''
  )
  const messageId = (
    explicitMessageId ||
    url.searchParams.get('message_id') ||
    url.searchParams.get('message') ||
    ''
  )
  return nativeNotificationUrlFromTarget({ chatId, topicId: topicId || null }, messageId)
}

export function nativePushUrlFromAction(action: ActionPerformed): string {
  const notification = action.notification
  const data = notification?.data || {}
  const taggedTarget = nativeNotificationTargetFromTag(stringValue(notification?.tag))
  const rawUrl = stringValue(data.url) || stringValue(notification?.link)
  return canonicalNativePushUrl(rawUrl, data, taggedTarget)
}

function nativePushReadTargetFromAction(action: ActionPerformed): NativeNotificationTarget | null {
  const notification = action.notification
  const data = (notification?.data || {}) as Record<string, unknown>
  const taggedTarget = nativeNotificationTargetFromTag(stringValue(notification?.tag))
  const explicitChatId = stringValue(data.chat_id) || taggedTarget?.chatId || null
  if (!explicitChatId) return null
  const explicitTopicId = (
    stringValue(data.topic_id) ||
    stringValue(data.topic) ||
    taggedTarget?.topicId ||
    null
  )
  return { chatId: explicitChatId, topicId: explicitTopicId }
}

function nativePrefetchTargetFromNotification(
  notification: PushNotificationSchema | null | undefined,
): NativeNotificationPrefetchTarget | null {
  const data = (notification?.data || {}) as Record<string, unknown>
  const taggedTarget = nativeNotificationTargetFromTag(stringValue(notification?.tag))
  const rawUrl = stringValue(data.url) || stringValue(notification?.link)
  let url: URL
  try {
    url = new URL(rawUrl?.trim() || '/chats', window.location.origin)
  } catch {
    url = new URL('/chats', window.location.origin)
  }
  const chatId = (
    stringValue(data.chat_id) ||
    url.searchParams.get('chat_id') ||
    chatIdFromPathname(url.pathname) ||
    taggedTarget?.chatId ||
    ''
  )
  if (!chatId) return null
  const topicId = (
    stringValue(data.topic_id) ||
    stringValue(data.topic) ||
    url.searchParams.get('topic_id') ||
    url.searchParams.get('topic') ||
    taggedTarget?.topicId ||
    null
  )
  const messageId = (
    stringValue(data.message_id) ||
    stringValue(data.message) ||
    url.searchParams.get('message_id') ||
    url.searchParams.get('message') ||
    null
  )
  return { chatId, topicId, messageId }
}

function syntheticNotificationOpenBundleUrl(target: NativeNotificationPrefetchTarget): string {
  const url = new URL(
    `/api/chats/${encodeURIComponent(target.chatId)}/notification-open-bundle`,
    window.location.origin,
  )
  if (target.topicId) url.searchParams.set('topic_id', target.topicId)
  if (target.messageId) url.searchParams.set('message_id', target.messageId)
  return `${url.pathname}${url.search}`
}

function nativeNotificationOpenBundleUrl(
  notification: PushNotificationSchema | null | undefined,
  fallbackTarget: NativeNotificationPrefetchTarget,
): string {
  const data = (notification?.data || {}) as Record<string, unknown>
  const raw = (
    stringValue(data.open_bundle_url) ||
    stringValue(data.notification_open_bundle_url) ||
    stringValue(data.bundle_url)
  )
  if (!raw) return syntheticNotificationOpenBundleUrl(fallbackTarget)
  try {
    const url = new URL(raw, window.location.origin)
    if (url.origin !== window.location.origin) return syntheticNotificationOpenBundleUrl(fallbackTarget)
    if (!/^\/api\/chats\/[^/]+\/notification-open-bundle$/.test(url.pathname)) {
      return syntheticNotificationOpenBundleUrl(fallbackTarget)
    }
    return `${url.pathname}${url.search}`
  } catch {
    return syntheticNotificationOpenBundleUrl(fallbackTarget)
  }
}

async function postMarkReadForNativeTarget(target: NativeNotificationTarget): Promise<void> {
  /* Mirror of the sw.js notificationclick mark-read POST. The Capacitor
     plugin handles its own notification tap on Android — sw.js never sees
     it — so without this fetch the backend only learns the message was
     read after ChatPage successfully mounts, which can be delayed, fail
     entirely on cold start, or already have happened before Android posts
     the tray card for a foreground FCM. POSTing here guarantees the cross-
     device fan-out runs from the native event itself. */
  try {
    const params = new URLSearchParams()
    if (target.topicId) params.set('topic_id', target.topicId)
    const qs = params.toString()
    await fetch(
      `/api/chats/${encodeURIComponent(target.chatId)}/mark-read${qs ? `?${qs}` : ''}`,
      { method: 'POST', credentials: 'include' },
    )
  } catch {
    /* ChatPage mount will retry mark-read */
  }
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const resp = await fetch(url, { credentials: 'include' })
    if (!resp.ok) return null
    return await resp.json() as T
  } catch {
    return null
  }
}

function isUnconfirmedAgentOutput(message: Message): boolean {
  const isAgentOutput = message.direction === 'outbound' || message.direction === 'agent'
  if (!isAgentOutput) return false
  return message.status !== 'received' && message.status !== 'replied' && message.status !== 'error'
}

function persistableMessages(messages: Message[]): Message[] {
  return messages.filter((message) => !isUnconfirmedAgentOutput(message))
}

function nativeTargetKey(target: NativeNotificationPrefetchTarget): string {
  return `${target.chatId}::${target.topicId ?? '__all__'}::${target.messageId ?? '__latest__'}`
}

function rememberNativeFullBundle(target: NativeNotificationPrefetchTarget): void {
  nativeFullBundleTargets.add(nativeTargetKey(target))
  if (nativeFullBundleTargets.size > 256) {
    const oldest = nativeFullBundleTargets.values().next().value
    if (typeof oldest === 'string') nativeFullBundleTargets.delete(oldest)
  }
}

function nativeTargetHasFullBundle(target: NativeNotificationPrefetchTarget | null): boolean {
  return Boolean(target && nativeFullBundleTargets.has(nativeTargetKey(target)))
}

function cachedNotificationOpenBundle(
  item: NativeNotificationCacheItem,
): NotificationOpenBundle | null {
  const value = item?.open_bundle
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : null
}

function nativeNotificationFromCacheItem(
  item: NativeNotificationCacheItem,
): PushNotificationSchema | null {
  const data = item?.data && typeof item.data === 'object' ? item.data : {}
  const chatId = stringValue(data.chat_id)
  if (!chatId) return null
  const messageId = stringValue(data.message_id) || stringValue(item.message_id)
  const title = stringValue(item.title) || stringValue(data.notification_title) || undefined
  const body = (
    stringValue(item.body) ||
    stringValue(data.notification_body) ||
    stringValue(data.body) ||
    undefined
  )
  return {
    id: messageId || stringValue(item.message_id) || undefined,
    title,
    body,
    tag: stringValue(item.tag) || undefined,
    data,
  } as PushNotificationSchema
}

export async function hydrateNativeNotificationCacheBeforeReactMount(): Promise<number> {
  if (!nativePushUsable() || !Capacitor.isPluginAvailable('AgentChatNativeNotificationCache')) {
    return 0
  }
  try {
    const result = await AgentChatNativeNotificationCache.drainPending()
    const items = Array.isArray(result?.items) ? result.items : []
    let hydrated = 0
    for (const item of items) {
      const notification = nativeNotificationFromCacheItem(item)
      if (!notification) continue
      const target = nativePrefetchTargetFromNotification(notification)
      const bundle = cachedNotificationOpenBundle(item)
      let fullBundleReady = false
      if (target && bundle) {
        fullBundleReady = await saveNotificationOpenBundle(bundle, target)
      }
      // Compatibility path for notifications delivered by an older APK that
      // stored only the bounded FCM preview. Fetch the final read model before
      // mounting/navigating; never render the preview and replace it seconds
      // later when an online fetch was possible.
      if (target && !fullBundleReady) {
        fullBundleReady = await prefetchNativeNotification(notification)
      }
      hydrated += 1
    }
    return hydrated
  } catch {
    return 0
  }
}

async function saveNotificationOpenBundle(
  bundle: NotificationOpenBundle | null,
  fallback: NativeNotificationPrefetchTarget,
): Promise<boolean> {
  if (!bundle) return false
  const chatId = bundle.chat_id || bundle.chat?.id || fallback.chatId
  if (!chatId) return false
  const topicId = bundle.topic_id || fallback.topicId || null
  const expectedMessageId = bundle.message_id || fallback.messageId || null
  const messages = persistableMessages(Array.isArray(bundle.messages) ? bundle.messages : [])

  await Promise.all([
    bundle.chat ? saveLocalChat(bundle.chat) : Promise.resolve(),
    Array.isArray(bundle.topics) ? saveLocalTopics(chatId, bundle.topics, { merge: true }) : Promise.resolve(),
    messages.length > 0
      ? saveLocalMessages(chatId, topicId, messages)
      : Promise.resolve(),
  ])
  dispatchNativeNotificationPrefetched({
    chat_id: chatId,
    topic_id: topicId,
    message_id: expectedMessageId,
    chat: bundle.chat || null,
    topics: Array.isArray(bundle.topics) ? bundle.topics : [],
    messages,
  })
  if (messages.length > 0) {
    rememberNativeFullBundle({ chatId, topicId, messageId: expectedMessageId })
    return true
  }
  return false
}

async function prefetchNativeNotification(
  notification: PushNotificationSchema,
): Promise<boolean> {
  const target = nativePrefetchTargetFromNotification(notification)
  if (!target) return false
  const bundleUrl = nativeNotificationOpenBundleUrl(notification, target)
  return saveNotificationOpenBundle(
    await fetchJson<NotificationOpenBundle>(bundleUrl),
    target,
  )
}

function rememberPendingOpenUrl(
  url: string,
  diagnostic?: { openId?: string | null; startMs?: number | null },
): void {
  pendingOpenUrls = [
    ...pendingOpenUrls,
    {
      url,
      diagnosticOpenId: diagnostic?.openId ?? null,
      diagnosticStartMs: diagnostic?.startMs ?? null,
    },
  ].slice(-MAX_PENDING_OPEN_URLS)
}

function dispatchNativePushOpen(
  url: string,
  diagnostic?: { openId?: string | null; startMs?: number | null },
): void {
  if (typeof window === 'undefined') return
  rememberPendingNotificationNav(url)
  const detail: NativePushOpenDetail = {
    url,
    handled: false,
    diagnosticOpenId: diagnostic?.openId ?? null,
    diagnosticStartMs: diagnostic?.startMs ?? null,
  }
  const event = new CustomEvent<NativePushOpenDetail>(OPEN_EVENT, {
    detail,
    cancelable: true,
  })
  window.dispatchEvent(event)
  if (!detail.handled && !event.defaultPrevented) {
    rememberPendingOpenUrl(url, diagnostic)
  }
}

function dispatchNativeNotificationPrefetched(detail: NativeNotificationPrefetchedDetail): void {
  if (typeof window === 'undefined') return
  window.dispatchEvent(new CustomEvent<NativeNotificationPrefetchedDetail>(PREFETCHED_EVENT, {
    detail,
  }))
}

export function drainPendingNativePushOpens(
  handleUrl: (
    url: string,
    diagnostic?: { openId?: string | null; startMs?: number | null },
  ) => void,
): void {
  const urls = pendingOpenUrls
  pendingOpenUrls = []
  for (const item of urls) {
    handleUrl(item.url, {
      openId: item.diagnosticOpenId ?? null,
      startMs: item.diagnosticStartMs ?? null,
    })
  }
}

export function nativePushOpenEventName(): string {
  return OPEN_EVENT
}

export function nativeNotificationPrefetchedEventName(): string {
  return PREFETCHED_EVENT
}

export function bindNativePushListeners(): void {
  if (!nativePushUsable() || listenersBound) return
  listenersBound = true
  void PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const readTarget = nativePushReadTargetFromAction(action)
    const openUrl = nativePushUrlFromAction(action)
    const notification = action.notification
    const notificationTitle = stringValue(notification?.title)
    const prefetchTarget = nativePrefetchTargetFromNotification(notification)
    const diagnosticStartMs = typeof performance !== 'undefined' ? performance.now() : Date.now()
    const diagnosticOpenId = prefetchTarget
      ? startNotificationOpenTiming({
          source: 'native',
          chatId: prefetchTarget.chatId,
          topicId: prefetchTarget.topicId,
          messageId: prefetchTarget.messageId,
          startMs: diagnosticStartMs,
          nativePlatform: Capacitor.isNativePlatform(),
        })
      : null
    void (async () => {
      const prefetchStartMs = typeof performance !== 'undefined' ? performance.now() : Date.now()
      await hydrateNativeNotificationCacheBeforeReactMount()
      let fullBundleReady = nativeTargetHasFullBundle(prefetchTarget)
      if (notification && !fullBundleReady) {
        fullBundleReady = await prefetchNativeNotification(notification)
      }
      const prefetchEndMs = typeof performance !== 'undefined' ? performance.now() : Date.now()
      updateNotificationOpenTiming(diagnosticOpenId, {
        prefetchMs: prefetchEndMs - prefetchStartMs,
      })
      if (prefetchTarget?.messageId && !fullBundleReady) {
        // A message-bearing tap may never navigate to bounded notification
        // chrome or stale local history. Keep the backend item unread so a
        // later healthy delivery can surface the real final answer.
        return
      }
      if (readTarget) {
        void postMarkReadForNativeTarget(readTarget)
        // Clear sibling notifications for the same scope locally so Mark
        // does not have to wait for the fan_out_read_clear FCM round-trip
        // before stale notifications disappear from the tray.
        void clearNativeNotificationsForChat(
          readTarget.chatId,
          readTarget.topicId,
          notificationTitle,
        ).catch(() => {})
      }
      dispatchNativePushOpen(openUrl, {
        openId: diagnosticOpenId,
        startMs: diagnosticStartMs,
      })
    })()
  })
  void PushNotifications.addListener('pushNotificationReceived', (notification) => {
    void handleNativePushReceived(notification)
  })
}

async function createAgentChatChannels(): Promise<void> {
  await Promise.all([
    PushNotifications.createChannel({
      id: CHANNEL_ID,
      name: 'AgentChat',
      description: 'Agent replies, approvals, and lane alerts',
      importance: 4,
      visibility: 1,
      vibration: true,
    }),
    PushNotifications.createChannel({
      id: EMAIL_DRAFT_CHANNEL_ID,
      name: 'Draft reply alerts',
      description: 'Ready email replies that need review',
      importance: 5,
      visibility: 1,
      vibration: true,
    }),
  ]).catch(() => undefined)
}

async function postNativeToken(token: string): Promise<void> {
  const appInfo = await nativeAppInfoForSubscription()
  const resp = await fetch('/api/push/subscribe', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      kind: 'fcm',
      endpoint: token,
      device_label: appInfo
        ? `AgentChat Android app ${appInfo.versionName}`
        : 'AgentChat Android app',
      user_agent: nativeUserAgentWithApkVersion(appInfo),
    }),
  })
  if (!resp.ok) throw new Error(`Native push subscribe failed: ${resp.status}`)
}

async function nativeAppInfoForSubscription(): Promise<NativeAppInfo | null> {
  if (!nativePushUsable() || !Capacitor.isPluginAvailable('AgentChatAppInfo')) return null
  try {
    return await AgentChatAppInfo.getInfo()
  } catch {
    return null
  }
}

export function nativeUserAgentWithApkVersion(info: NativeAppInfo | null): string {
  if (!info) return navigator.userAgent
  const versionCode = Number(info.versionCode)
  if (!Number.isFinite(versionCode) || versionCode <= 0) return navigator.userAgent
  const versionName = String(info.versionName || '').replace(/[()]/g, '').trim()
  // This is the first APK whose Firebase service persists the full signed
  // response bundle before posting a data-only notification. Old APKs must
  // keep receiving Android-managed notification payloads during the rollout.
  const dataOnlyGate = versionCode >= 1_787_337_314
    ? ' AgentChatNativeDataOnly/1'
    : ''
  const suffix = versionName
    ? ` AgentChatApk/${Math.trunc(versionCode)} (${versionName})`
    : ` AgentChatApk/${Math.trunc(versionCode)}`
  return `${navigator.userAgent}${suffix}${dataOnlyGate}`
}

async function nextRegistrationToken(): Promise<string> {
  let cleanup = async () => {}
  const token = new Promise<string>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      reject(new Error('Native push registration timed out'))
    }, 15_000)
    const done = (fn: () => void) => {
      window.clearTimeout(timeout)
      void cleanup()
      fn()
    }
    const registration = PushNotifications.addListener('registration', (t) => {
      done(() => resolve(t.value))
    })
    const registrationError = PushNotifications.addListener('registrationError', (err) => {
      done(() => reject(new Error(err.error || 'Native push registration failed')))
    })
    cleanup = async () => {
      const handles = await Promise.allSettled([registration, registrationError])
      for (const handle of handles) {
        if (handle.status === 'fulfilled') await handle.value.remove()
      }
    }
  })
  try {
    await PushNotifications.register()
  } catch (e) {
    await cleanup()
    throw e
  }
  return token
}

async function registerAndStoreNativeToken(): Promise<string> {
  await createAgentChatChannels()
  const token = await nextRegistrationToken()
  await postNativeToken(token)
  return token
}

export async function getNativePushPermission(): Promise<NativePermission> {
  if (!nativePushUsable()) return 'unavailable'
  const status = await PushNotifications.checkPermissions()
  if (status.receive === 'granted') return 'granted'
  if (status.receive === 'denied') return 'denied'
  return 'prompt'
}

export async function enableNativePush(): Promise<void> {
  if (!nativePushUsable()) throw new Error('Native push is unavailable')
  bindNativePushListeners()
  let status = await PushNotifications.checkPermissions()
  if (status.receive === 'prompt') status = await PushNotifications.requestPermissions()
  if (status.receive !== 'granted') throw new Error(`Permission ${status.receive}`)
  await registerAndStoreNativeToken()
}

export async function syncGrantedNativePushRegistration(): Promise<void> {
  if (!nativePushUsable() || bootSyncStarted) return
  bootSyncStarted = true
  bindNativePushListeners()
  const status = await PushNotifications.checkPermissions()
  if (status.receive !== 'granted') return
  await registerAndStoreNativeToken()
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

function notificationDataString(notification: PushNotificationSchema, key: string): string | null {
  const data = notification.data
  const value = data?.[key]
  return typeof value === 'string' && value.trim() ? value : null
}

function notificationDataFlag(notification: PushNotificationSchema, key: string): boolean {
  const value = notification.data?.[key]
  return value === true || value === 1 || value === '1' || value === 'true'
}

export async function handleNativePushReceived(notification: PushNotificationSchema): Promise<void> {
  const kind = notificationDataString(notification, 'kind')
  const control = notificationDataString(notification, 'control')
  if (kind !== 'chat_read' && control !== 'clear_read_notifications') {
    const visibleTarget = nativePrefetchTargetFromNotification(notification)
    await hydrateNativeNotificationCacheBeforeReactMount()
    if (visibleTarget && nativeNotificationTargetIsVisible(visibleTarget)) {
      await postMarkReadForNativeTarget(visibleTarget)
      await clearNativeNotificationsForChat(
        visibleTarget.chatId,
        visibleTarget.topicId,
        stringValue(notification.title),
      )
    }
    if (!nativeTargetHasFullBundle(visibleTarget)) {
      await prefetchNativeNotification(notification)
    }
    return
  }

  const chatId = notificationDataString(notification, 'chat_id')
  if (!chatId) return
  const topicId = notificationDataString(notification, 'topic_id')
  const allTopics = notificationDataFlag(notification, 'all_topics')

  if (allTopics) {
    await clearNativeNotificationsForChat(chatId, null)
  } else if (topicId) {
    await clearNativeNotificationsForChat(chatId, topicId)
  } else {
    await clearNativeNotificationsForChats(
      [chatId],
      { includeTopicNotifications: false },
    )
  }
}

function nativeNotificationUrl(notification: PushNotificationSchema): URL | null {
  const rawUrl = (
    stringValue(notification.link) ||
    notificationDataString(notification, 'url') ||
    notificationDataString(notification, 'link')
  )
  if (!rawUrl) return null
  try {
    return new URL(rawUrl, window.location.origin)
  } catch {
    return null
  }
}

function urlBelongsToChat(url: URL | null, chatId: string): boolean {
  if (!url) return false
  const pathMatch = url.pathname.match(/^\/chats\/([^/]+)$/)
  const pathChatId = pathMatch?.[1] ? decodeURIComponent(pathMatch[1]) : null
  return pathChatId === chatId || url.searchParams.get('chat_id') === chatId
}

function urlBelongsToTopic(url: URL | null, chatId: string, topicId: string): boolean {
  if (!url || !urlBelongsToChat(url, chatId)) return false
  const urlTopicId = url.searchParams.get('topic_id') || url.searchParams.get('topic') || ''
  return urlTopicId === topicId
}

function urlHasTopic(url: URL | null, chatId: string): boolean {
  if (!url || !urlBelongsToChat(url, chatId)) return false
  return Boolean(url.searchParams.get('topic_id') || url.searchParams.get('topic'))
}

function nativeNotificationHasTopicTarget(
  notification: PushNotificationSchema,
  chatId: string,
): boolean {
  const tag = stringValue(notification.tag) || ''
  const group = stringValue(notification.group) || ''
  const topicTagPrefix = `chat-${chatId}-topic-`
  if (tag.startsWith(topicTagPrefix) || group.startsWith(topicTagPrefix)) return true
  if (
    notificationDataString(notification, 'chat_id') === chatId &&
    Boolean(notificationDataString(notification, 'topic_id'))
  ) {
    return true
  }
  return urlHasTopic(nativeNotificationUrl(notification), chatId)
}

type NativeNotificationMatchOptions = {
  includeTopicNotifications?: boolean
  chatLabel?: string | null
}

function nativeNotificationBelongsToChat(
  notification: PushNotificationSchema,
  chatId: string,
  chatLabel?: string | null,
  options: NativeNotificationMatchOptions = {},
): boolean {
  if (
    options.includeTopicNotifications === false &&
    nativeNotificationHasTopicTarget(notification, chatId)
  ) {
    return false
  }
  const tag = stringValue(notification.tag) || ''
  const group = stringValue(notification.group) || ''
  const chatTag = `chat-${chatId}`
  if (
    tag === chatId ||
    tag === chatTag ||
    tag.startsWith(`${chatTag}-topic-`) ||
    group === chatId ||
    group === chatTag ||
    group.startsWith(`${chatTag}-topic-`)
  ) {
    return true
  }
  if (notificationDataString(notification, 'chat_id') === chatId) return true
  if (urlBelongsToChat(nativeNotificationUrl(notification), chatId)) return true

  // Android/FCM-delivered notifications sometimes expose the rendered
  // notification title but not the original FCM data extras. For normal
  // user-driven chats, the title is stable ("Codex #5", "Claude #2") and
  // is enough to remove the exact stale notification Mark just opened.
  const title = stringValue(notification.title) || ''
  const optionLabel = options.chatLabel ?? null
  return Boolean(
    (chatLabel && title.includes(chatLabel)) ||
    (optionLabel && title.includes(optionLabel)),
  )
}

function nativeNotificationBelongsToTopic(
  notification: PushNotificationSchema,
  chatId: string,
  topicId: string,
): boolean {
  const tag = stringValue(notification.tag) || ''
  if (tag === `chat-${chatId}-topic-${topicId}`) return true
  return (
    nativeNotificationBelongsToChat(notification, chatId) &&
    (
      notificationDataString(notification, 'topic_id') === topicId ||
      urlBelongsToTopic(nativeNotificationUrl(notification), chatId, topicId)
    )
  )
}

/**
 * Clear OS notifications for a specific chat (and optionally a topic) in the
 * native APK context. Capacitor's PushNotifications plugin reaches
 * NotificationManager directly — the service-worker getNotifications() path
 * is blind to FCM-delivered native notifications so this is the only way.
 *
 * Filters by backend-supplied tag/data/url fields. When topicId is supplied,
 * the match is topic-scoped; otherwise the caller can opt out of topic
 * notifications for conservative read-state sweepers.
 */
export async function clearNativeNotificationsForChat(
  chatId: string,
  topicId: string | null,
  chatLabel?: string | null,
): Promise<void> {
  if (!nativePushUsable()) return
  const { notifications } = await PushNotifications.getDeliveredNotifications()
  const toRemove = notifications.filter((n) => {
    if (topicId) return nativeNotificationBelongsToTopic(n, chatId, topicId)
    return nativeNotificationBelongsToChat(n, chatId, chatLabel)
  })
  if (toRemove.length === 0) return
  await PushNotifications.removeDeliveredNotifications({ notifications: toRemove })
}

export async function clearNativeNotificationsForChats(
  chatIds: string[],
  options: NativeNotificationMatchOptions = {},
): Promise<void> {
  if (!nativePushUsable()) return
  const ids = new Set(chatIds.filter(Boolean))
  if (ids.size === 0) return
  const { notifications } = await PushNotifications.getDeliveredNotifications()
  const toRemove = notifications.filter((n) => {
    for (const chatId of ids) {
      if (nativeNotificationBelongsToChat(n, chatId, null, options)) return true
    }
    return false
  })
  if (toRemove.length === 0) return
  await PushNotifications.removeDeliveredNotifications({ notifications: toRemove })
}
